import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Provider } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { api_path } from '../../global';

const PUBLIC_REGISTRY_PATH = '/organization/public_registry/';
export const REGISTRY_SESSION_URL = `${api_path}/organization/public_registry/session`;
export const REGISTRY_SESSION_REQUIRED_STATUS = 428;
export const REGISTRY_SESSION_REQUIRED_CODE_NAME = 'REGISTRY_SESSION_REQUIRED';
export const REGISTRY_ACCESS_HEADER = 'X-Registry-Access';

/**
 * Makes sure the API has issued the signed registry token before calling the public registry
 * endpoints, and transparently renews it once when the API answers 428.
 * The token is kept in memory and sent in the X-Registry-Access header: inside a third-party
 * iframe the browser rejects the API's SameSite=Lax cookie, so the cookie alone does not work there.
 */
@Injectable()
export class RegistryAccessInterceptor implements HttpInterceptor {
  private session$: Observable<boolean> | null = null;
  private token: string | null = null;
  // Bypasses the interceptor chain, so the session call is not intercepted again
  private readonly httpClient: HttpClient;

  constructor(httpBackend: HttpBackend) {
    this.httpClient = new HttpClient(httpBackend);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isPublicRegistryRequest(request)) {
      return next.handle(request);
    }

    return this.ensureSession().pipe(
      switchMap(() => this.sendWithToken(request, next)),
      catchError((error) =>
        this.isSessionRequiredError(error)
          ? this.refreshSession().pipe(switchMap(() => this.sendWithToken(request, next)))
          : throwError(() => error),
      ),
    );
  }

  // Reads this.token at send time, so a retry after a renewed session carries the new token
  private sendWithToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const withToken = this.token ? request.clone({ setHeaders: { [REGISTRY_ACCESS_HEADER]: this.token } }) : request;

    return next.handle(withToken).pipe(
      // The API re-issues the token on registry responses (sliding refresh)
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.storeToken(event.headers);
        }
      }),
    );
  }

  private storeToken(headers: HttpHeaders): void {
    const token = headers.get(REGISTRY_ACCESS_HEADER);
    if (token) {
      this.token = token;
    }
  }

  private isPublicRegistryRequest(request: HttpRequest<any>): boolean {
    return request.url.includes(PUBLIC_REGISTRY_PATH) && request.url !== REGISTRY_SESSION_URL;
  }

  private isSessionRequiredError(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === REGISTRY_SESSION_REQUIRED_STATUS &&
      error.error?.code_name === REGISTRY_SESSION_REQUIRED_CODE_NAME
    );
  }

  private ensureSession(): Observable<boolean> {
    if (!this.session$) {
      this.session$ = this.requestSession().pipe(shareReplay(1));
    }
    return this.session$;
  }

  private refreshSession(): Observable<boolean> {
    this.session$ = null;
    return this.ensureSession();
  }

  private requestSession(): Observable<boolean> {
    return this.httpClient.post(REGISTRY_SESSION_URL, null, { withCredentials: true, observe: 'response' }).pipe(
      tap((response) => this.storeToken(response.headers)),
      map(() => true),
      // Never break the registry because of the session call; let the next request try again
      catchError(() => {
        this.session$ = null;
        return of(false);
      }),
    );
  }
}

export const RegistryAccessInterceptorService: Provider = {
  provide: HTTP_INTERCEPTORS,
  useClass: RegistryAccessInterceptor,
  multi: true,
};
